import React, { useState, useEffect } from "react";
import LeadsMatchesTable from "./tables/LeadsMatchesTable";
import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import DashboardAdminCalendar from "../../../calendar/DashboardAdminCalendar";
import { leadsDataCard, comparedYearData, calendarData } from "../adminData";
import {
  useGetAllLeadsQuery,
  useGetFilteredLeadsQuery,
  useLazyGetAllLeadsQuery,
  useLazyGetFilteredLeadsQuery,
} from "../../../../store/admin/leads";
import DropDownFilters from "../dashboardAdminUser/DropDownFilters";
import { useGetAllUserStatusesQuery } from "../../../../store/admin/userStatuses";
import moment from "moment";
import { formatDate } from "../../../../utilities/utils";

const LeadMatchDataPage = () => {
  const [filterActive, setFilterActive] = useState(false);
  const [allLeadsData, setAllLeadsData] = useState();
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const currentDate = moment();
  const startOfMonth = currentDate.startOf('month');
  const [selectRange, setSelectRange] = useState({
    startDate:  moment().format("MM/DD/YYYY"),
    endDate: moment().format("MM/DD/YYYY"),
  });

  const [selectedCalendarFilter, setSelectedCalendarFilter] =
    useState("Today");
  const [selectedCompareFilter, setSelectedCompareFilter] = useState([
    "Previous period",
  ]);

  const formattedStartDate = moment(
    selectRange?.startDate,
    "MM/DD/YYYY"
  ).format("YYYY-MM-DD");
  const formattedEndDate = moment(selectRange?.endDate, "MM/DD/YYYY").format(
    "YYYY-MM-DD"
  );

  const {
    data: allFilteredLeadsInfo,
    isLoading: allFilteredLeadsInfoLoader,
    isSuccess: allFilteredLeadsInfoSuccess,
  } = useGetFilteredLeadsQuery({
    startDate: startOfMonth.format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  const [refetch, { data, isLoading: allLeadsRefetchLoader, isFetching }] =
    useLazyGetAllLeadsQuery();

  const { data: allUserStatuses } = useGetAllUserStatusesQuery();

  const [
    getAllFilteredLeads,
    {
      data: filteredLeads,
      isLoading: filteredLeadsLoader,
      isSuccess: filteredLeadsSuccess,
      isFetching: filteredLeadsFetching,
    },
  ] = useLazyGetFilteredLeadsQuery();

  useEffect(() => {
    if (filteredLeadsSuccess && !filteredLeadsLoader) {
      setCalendarOpen(false);
    }
  }, [filteredLeadsSuccess, filteredLeadsFetching]);

  useEffect(() => {
    if (filteredLeads) {
      setAllLeadsData(filteredLeads);
    }
  }, [filteredLeads]);

  useEffect(() => {
    if (allFilteredLeadsInfo) {
      setAllLeadsData(allFilteredLeadsInfo);
    }
  }, [allFilteredLeadsInfo]);

  useEffect(() => {
    if (data) {
      setAllLeadsData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isFetching) {
      setAllLeadsData({});
    }
  }, [isFetching]);

  const calendarFilterChange = (value) => {
    setSelectedCalendarFilter(value);

    const selectedPeriod =
      calendarData && calendarData?.find((item) => item.text === value)?.period;

    if (selectedPeriod) {
      setSelectRange({
        startDate: selectedPeriod.start,
        endDate: selectedPeriod.end,
      });
    } else {
      setSelectRange({});
    }
  };

  const compareFilterChange = (value) => {
    setSelectedCompareFilter(value);
  };

  const handleProfessionalFilter = () => {
    setFilterActive((prevState) => !prevState);
  };

  const handleCalendarChange = (value, event) => {
    const startDate = formatDate(value[0]);
    const endDate = formatDate(value[1]);
    setSelectRange({ startDate: startDate, endDate: endDate });
  };

  const handleDateFilter = () => {
    const formattedStartDate = moment(
      selectRange.startDate,
      "MM/DD/YYYY"
    ).format("YYYY-MM-DD");
    const formattedEndDate = moment(selectRange.endDate, "MM/DD/YYYY").format(
      "YYYY-MM-DD"
    );

    if (selectedCalendarFilter === "All") {
      getAllFilteredLeads({});
    } else {
      getAllFilteredLeads({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        // status_ids: selectedStatus.map((status) => status.id).join(","),
      });
    }
  };

  const handleOpen = () => {
    setCalendarOpen((prevState) => !prevState);
  };

  const handleClose = () => {
    setCalendarOpen(false);
  };

  return (
    <div className="col-span-12">
      <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-5 mb-5">
        <h3 className="text-xl leading-[160%] font-bold text-[#04091E]">
          Leads/Match Data
        </h3>
        <div>
          <DashboardAdminCalendar
            calendarFilter={calendarData}
            compareFilter={comparedYearData}
            selectedCalendarFilter={selectedCalendarFilter}
            calendarFilterChange={calendarFilterChange}
            selectedCompareFilter={selectedCompareFilter}
            compareFilterChange={compareFilterChange}
            handleCalendarChange={handleCalendarChange}
            setSelectRange={setSelectRange}
            selectRange={selectRange}
            handleDateFilter={handleDateFilter}
            calendarOpen={calendarOpen}
            setCalendarOpen={setCalendarOpen}
            handleOpen={handleOpen}
            handleClose={handleClose}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-5 xxl:gap-6 mb-[30px]">
        {leadsDataCard.map((card) => (
          <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-4 rounded-md">
            <span className="text-xs leading-[160%] text-[#485868] font-bold uppercase mb-1">
              {card.subTitle}
            </span>
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
              {card.title}
            </h6>
            <div className="flex justify-between items-end">
              <div className="flex items-center gap-4">
                <span className="text-[32px] leading-[125%] font-black text-customGray">
                  {card.price}
                </span>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <card.chartImg />
                    <span
                      className="text-sm font-bold leading-[180%]"
                      style={{ color: card.perColor }}
                    >
                      {card.percentage}
                    </span>
                  </div>
                  <p className="text-xs font-medium leading-[160%] text-[#485868]">
                    {card.period}
                  </p>
                </div>
              </div>
              <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
                <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
                <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between items-start md:items-center gap-5 mb-[14px]">
        <h3 className="text-xl leading-[160%] font-bold text-[#04091E] capitalize">
          Successful Matches
        </h3>
        <div className="relative">
          <button
            className={`w-12 h-12 flex items-center justify-center gap-2.5 md:gap-0 rounded-[10px] col-span-6 transition-all ${
              filterActive
                ? "bg-[#D2ECFF] text-primary"
                : "bg-[#EBEFF3] text-[#485868]"
            }`}
            onClick={handleProfessionalFilter}
          >
            <AdjustmentsVerticalIcon className="w-6 h-6" />
            {/* <span
              className={`text-sm font-bold leading-[160%] block md:hidden ${
                filterActive ? "text-primary" : "text-[#485868]"
              }`}
            >
              Filter
            </span> */}
          </button>
          <div
            className={`notificationDropdown absolute right-0 top-[55px] md:top-[60px] z-[380] opacity-0 ${
              filterActive ? "notificationOpen" : ""
            }`}
          >
            <DropDownFilters
              setFilterActive={setFilterActive}
              filterActive={filterActive}
              statusData={allUserStatuses}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              setCreditRenewal={false}
              // applyFiltered={applyFiltered}
            />
          </div>
        </div>
      </div>
      <LeadsMatchesTable
        leadsData={allLeadsData}
        getUpdateData={(page) => {
          getAllFilteredLeads({
            page,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            // status_ids: selectedStatus.map((status) => status.id).join(","),
          });
        }}
      />
    </div>
  );
};

export default LeadMatchDataPage;
